// Flight itineraries on the general briefing.
//
// The producer books flights for (some of) the crew on a foreign gig.
// One row in `briefing_flights` is ONE LEG: a direct flight is a single
// leg, a flight with a stopover is two legs sharing the same direction.
// `briefing_flight_passengers` says who is on that leg, with the fields
// that differ per person (booking reference, seat, baggage allowance).
//
// This module owns the shape + the derivations (duration, layover,
// grouping, validation) once, shared between the producer editor, the
// read-only portal view, the crew app and the briefing PDF — so the
// four never drift on "what does 2h15 mean" or "which leg comes first".
//
// Everything here is pure: no Supabase, no React, no Date.now() except
// where a caller passes the reference instant in explicitly. That keeps
// it testable and safe to import from the Deno edge runtime.

/** Towards the festival, or back home. */
export type FlightDirection = "outbound" | "return";

export const FLIGHT_DIRECTIONS: readonly FlightDirection[] = ["outbound", "return"];

/** Heading for each direction. One definition, so the producer editor,
 * the portal, the crew app and the PDF can't drift on wording. */
export const FLIGHT_DIRECTION_LABEL: Record<FlightDirection, string> = {
  outbound: "Outbound",
  return: "Return",
};

/** One leg of a journey. Mirrors a `briefing_flights` row. */
export interface FlightLeg {
  id: string;
  briefing_id: string;
  direction: FlightDirection;
  /** 0 for the first leg; 1.. for each leg after a stopover. */
  leg_order: number;
  airline: string | null;
  flight_number: string | null;
  departure_airport: string | null;
  departure_airport_name: string | null;
  departure_terminal: string | null;
  /** Check-in desk(s) at departure, e.g. "9-16". Departure only — no
   * airline publishes an arrival check-in desk. */
  departure_check_in_desk: string | null;
  departure_at: string | null;
  arrival_airport: string | null;
  arrival_airport_name: string | null;
  arrival_terminal: string | null;
  arrival_at: string | null;
  /** UTC offset in minutes at the departure airport, for local rendering. */
  departure_utc_offset_minutes: number | null;
  /** UTC offset in minutes at the arrival airport, for local rendering. */
  arrival_utc_offset_minutes: number | null;
  duration_minutes: number | null;
  aircraft: string | null;
  notes: string | null;
}

/** One person on one leg. Mirrors a `briefing_flight_passengers` row. */
export interface FlightPassenger {
  id: string;
  flight_id: string;
  briefing_crew_id: string;
  booking_reference: string | null;
  seat: string | null;
  baggage: string | null;
  special_baggage: string | null;
}

/** A leg together with the current reader's own passenger details. */
export interface FlightLegWithPassenger {
  leg: FlightLeg;
  passenger: FlightPassenger | null;
}

/** All legs of one direction, in travel order. */
export interface FlightItinerary {
  direction: FlightDirection;
  legs: FlightLegWithPassenger[];
}

// ── Parsing ──────────────────────────────────────────────────────────

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function int(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return Math.trunc(parsed);
  }
  return null;
}

function isDirection(value: unknown): value is FlightDirection {
  return value === "outbound" || value === "return";
}

/**
 * Defensive parse of a raw DB row into a FlightLeg. Rows are read on
 * crew devices and in the PDF renderer, so a malformed row must degrade
 * to nulls rather than throw. Rows without an id or a valid direction
 * are unusable and are dropped by `parseFlightLegs`.
 */
export function parseFlightLeg(row: unknown): FlightLeg | null {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  const r = row as Record<string, unknown>;
  const id = str(r.id);
  if (!id || !isDirection(r.direction)) return null;
  return {
    id,
    briefing_id: str(r.briefing_id) ?? "",
    direction: r.direction,
    leg_order: int(r.leg_order) ?? 0,
    airline: str(r.airline),
    flight_number: str(r.flight_number),
    departure_airport: str(r.departure_airport),
    departure_airport_name: str(r.departure_airport_name),
    departure_terminal: str(r.departure_terminal),
    departure_check_in_desk: str(r.departure_check_in_desk),
    departure_at: str(r.departure_at),
    arrival_airport: str(r.arrival_airport),
    arrival_airport_name: str(r.arrival_airport_name),
    arrival_terminal: str(r.arrival_terminal),
    arrival_at: str(r.arrival_at),
    departure_utc_offset_minutes: int(r.departure_utc_offset_minutes),
    arrival_utc_offset_minutes: int(r.arrival_utc_offset_minutes),
    duration_minutes: int(r.duration_minutes),
    aircraft: str(r.aircraft),
    notes: str(r.notes),
  };
}

export function parseFlightLegs(rows: unknown): FlightLeg[] {
  if (!Array.isArray(rows)) return [];
  return rows.map(parseFlightLeg).filter((leg): leg is FlightLeg => leg !== null);
}

export function parseFlightPassenger(row: unknown): FlightPassenger | null {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  const r = row as Record<string, unknown>;
  const id = str(r.id);
  const flightId = str(r.flight_id);
  const crewId = str(r.briefing_crew_id);
  if (!id || !flightId || !crewId) return null;
  return {
    id,
    flight_id: flightId,
    briefing_crew_id: crewId,
    booking_reference: str(r.booking_reference),
    seat: str(r.seat),
    baggage: str(r.baggage),
    special_baggage: str(r.special_baggage),
  };
}

export function parseFlightPassengers(rows: unknown): FlightPassenger[] {
  if (!Array.isArray(rows)) return [];
  return rows.map(parseFlightPassenger).filter((p): p is FlightPassenger => p !== null);
}

// ── Derivations ──────────────────────────────────────────────────────

/**
 * Minutes between two ISO instants, or null when either is missing or
 * unparseable. Both timestamps are absolute instants (timestamptz), so
 * this is timezone-correct across a flight that crosses zones — the
 * classic "AMS 10:00 → LAS 12:30" that is 11.5 hours, not 2.5.
 *
 * A negative result means the data is wrong (arrival before departure)
 * and returns null; `flightLegIssues` reports it as a validation issue
 * instead of silently rendering "-3h".
 */
export function minutesBetween(fromISO: string | null, toISO: string | null): number | null {
  if (!fromISO || !toISO) return null;
  const from = Date.parse(fromISO);
  const to = Date.parse(toISO);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  const minutes = Math.round((to - from) / 60_000);
  return minutes < 0 ? null : minutes;
}

/**
 * The duration to show for a leg. Prefers the timestamps (always
 * consistent with what the crew reads above it) and falls back to the
 * cached `duration_minutes` from the lookup API when a timestamp is
 * missing.
 */
export function legDurationMinutes(leg: FlightLeg): number | null {
  return minutesBetween(leg.departure_at, leg.arrival_at) ?? leg.duration_minutes;
}

/** "2h 15m", "45m", "3h". Null-safe: returns "" for unknown durations. */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return "";
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

// ── Local airport time ───────────────────────────────────────────────

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export interface LocalMoment {
  /** "Mon 10 Aug" */
  date: string;
  /** "08:00" */
  time: string;
  /** "Mon 10 Aug, 08:00" */
  full: string;
}

const UNKNOWN_MOMENT: LocalMoment = { date: "", time: "", full: "" };

/**
 * Format an instant as the wall-clock time at the airport it belongs to.
 *
 * A boarding pass says "departs 08:00" in the departure airport's own
 * time. Rendering the stored instant in the READER's timezone would
 * show a Dutch crew member their Las Vegas departure as 17:00 — the
 * same instant, but not what any ticket, screen or gate agent says. So
 * the offset that was in force at that airport is applied explicitly.
 *
 * When the offset is unknown (a legacy row, or a manual entry saved
 * before offsets were captured) `fallbackOffsetMinutes` is used instead
 * of the reader's own timezone, so the output stays deterministic and
 * testable regardless of which machine renders it.
 *
 * Deliberately hand-rolled instead of Intl with a timeZone: we store a
 * numeric offset, not an IANA zone name, and this must produce byte-
 * identical output on the web, in React Native (whose Intl support is
 * partial on Android) and in the Deno PDF renderer.
 */
export function formatLocalMoment(
  iso: string | null,
  offsetMinutes: number | null,
  fallbackOffsetMinutes = 0,
): LocalMoment {
  if (!iso) return UNKNOWN_MOMENT;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return UNKNOWN_MOMENT;

  const offset = offsetMinutes ?? fallbackOffsetMinutes;
  const shifted = new Date(parsed + offset * 60_000);

  const date = `${WEEKDAYS[shifted.getUTCDay()]} ${shifted.getUTCDate()} ${
    MONTHS[shifted.getUTCMonth()]
  }`;
  const time =
    `${String(shifted.getUTCHours()).padStart(2, "0")}:` +
    `${String(shifted.getUTCMinutes()).padStart(2, "0")}`;

  return { date, time, full: `${date}, ${time}` };
}

/** "+02:00" / "-07:00" / "" — shown next to a time so a cross-timezone
 * itinerary can't be misread. */
export function formatUtcOffset(offsetMinutes: number | null): string {
  if (offsetMinutes == null || !Number.isFinite(offsetMinutes)) return "";
  const sign = offsetMinutes < 0 ? "-" : "+";
  const abs = Math.abs(offsetMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, "0");
  const minutes = String(abs % 60).padStart(2, "0");
  return `${sign}${hours}:${minutes}`;
}

/**
 * True when the two ends of a leg sit in different UTC offsets — the
 * case where showing local times without a marker misleads. Callers use
 * it to decide whether to print the offset next to each time.
 */
export function crossesTimezone(leg: FlightLeg): boolean {
  const from = leg.departure_utc_offset_minutes;
  const to = leg.arrival_utc_offset_minutes;
  if (from == null || to == null) return false;
  return from !== to;
}

/**
 * True when a leg has a real departure but no terminal and no check-in
 * desk.
 *
 * Airports publish those only days before departure, and for a booking
 * made months ahead they are simply not knowable yet. Without a marker
 * the crew card just omits them, which reads as "the producer forgot" —
 * so callers use this to show "announced at the airport" instead of a
 * silent gap. Requires a departure time: a leg with nothing filled in at
 * all is incomplete, not pending.
 */
export function departureGateUnknown(
  leg: Pick<FlightLeg, "departure_at" | "departure_terminal" | "departure_check_in_desk">,
): boolean {
  return !!leg.departure_at && !leg.departure_terminal && !leg.departure_check_in_desk;
}

/**
 * The departure/arrival moment of a leg, in that airport's local time.
 *
 * Wraps formatLocalMoment so the "what clock do we show when the offset
 * was never captured?" decision lives here instead of at each call site.
 * It used to be made three times — the crew app fell back to UTC, the
 * producer page to the viewer's timezone, the PDF to UTC — so the same
 * flight could show two different hours to two people, with nothing
 * failing to signal it.
 *
 * UTC is the fallback: an instant is only ever stored together with the
 * offset it was derived from, so a missing offset means the row predates
 * that rule and has no better answer available.
 */
export function legMoment(leg: FlightLeg, end: "departure" | "arrival"): LocalMoment {
  return end === "departure"
    ? formatLocalMoment(leg.departure_at, leg.departure_utc_offset_minutes)
    : formatLocalMoment(leg.arrival_at, leg.arrival_utc_offset_minutes);
}

/**
 * "in 3 days" / "in 5 hours" / "" once it has departed. `now` is passed
 * in so the result is deterministic to test.
 */
export function countdownLabel(departureISO: string | null, now: number): string {
  if (!departureISO) return "";
  const departure = Date.parse(departureISO);
  if (!Number.isFinite(departure)) return "";
  const minutes = Math.round((departure - now) / 60_000);
  if (minutes < 0) return "";
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours} ${hours === 1 ? "hour" : "hours"}`;
  const days = Math.round(hours / 24);
  return `in ${days} ${days === 1 ? "day" : "days"}`;
}

/** The first departure of an itinerary — what a countdown counts to. */
export function firstDeparture(itinerary: FlightItinerary): string | null {
  return itinerary.legs[0]?.leg.departure_at ?? null;
}

/** Ground time between two consecutive legs, in minutes. */
export function layoverMinutes(previous: FlightLeg, next: FlightLeg): number | null {
  return minutesBetween(previous.arrival_at, next.departure_at);
}

/**
 * "KL 1234" / "kl1234" / " KL-1234 " → "KL1234".
 * The lookup API only accepts the compact uppercase form, and storing
 * one canonical form keeps the crew card from showing three spellings
 * of the same flight.
 */
export function normalizeFlightNumber(value: string | null | undefined): string {
  if (!value) return "";
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/** "AMS → BCN", or as much of it as is known. */
export function routeLabel(leg: FlightLeg): string {
  const from = leg.departure_airport ?? leg.departure_airport_name ?? "?";
  const to = leg.arrival_airport ?? leg.arrival_airport_name ?? "?";
  return `${from} → ${to}`;
}

/**
 * What to hand a maps app to navigate to an airport.
 *
 * The airport name column holds the CITY ("Amsterdam", "Vienna") — that
 * is what a crew member recognises from their ticket. Handing that
 * straight to a maps app routes them to the city centre, which is not
 * where the flight leaves from. Adding the word "Airport" plus the IATA
 * code in brackets is what both Apple and Google Maps resolve to the
 * terminal.
 *
 * Returns "" when neither is known, so callers can hide the button
 * instead of opening a maps app on a blank search.
 */
export function airportSearchQuery(iataCode: string | null, city: string | null): string {
  if (city && iataCode) return `${city} Airport (${iataCode})`;
  if (iataCode) return `${iataCode} Airport`;
  if (city) return `${city} Airport`;
  return "";
}

/** "KL1234" or "KLM KL1234" when the airline is known. */
export function flightLabel(leg: FlightLeg): string {
  const number = normalizeFlightNumber(leg.flight_number);
  if (leg.airline && number) return `${leg.airline} ${number}`;
  return leg.airline ?? number;
}

/**
 * A leg the producer started but left completely blank must not render
 * as an empty card in the crew app.
 */
export function hasFlightContent(leg: FlightLeg): boolean {
  return !!(
    leg.airline ||
    leg.flight_number ||
    leg.departure_airport ||
    leg.departure_airport_name ||
    leg.departure_at ||
    leg.arrival_airport ||
    leg.arrival_airport_name ||
    leg.arrival_at ||
    leg.notes
  );
}

// ── Grouping ─────────────────────────────────────────────────────────

/**
 * Travel order within a direction: by `leg_order` first (the producer's
 * explicit ordering), then by departure time as a tiebreak so two legs
 * saved with the same order still render sensibly. Legs without a
 * departure time sort last — an unfilled leg belongs at the bottom.
 */
function compareLegs(a: FlightLeg, b: FlightLeg): number {
  if (a.leg_order !== b.leg_order) return a.leg_order - b.leg_order;
  const aTime = a.departure_at ? Date.parse(a.departure_at) : Number.POSITIVE_INFINITY;
  const bTime = b.departure_at ? Date.parse(b.departure_at) : Number.POSITIVE_INFINITY;
  if (aTime !== bTime) return aTime - bTime;
  return a.id.localeCompare(b.id);
}

/**
 * Build the two itineraries (outbound, return) for one reader, pairing
 * every leg with that reader's own passenger row.
 *
 * `briefingCrewId` scopes the result to ONE person: only legs they are
 * a passenger on are returned, each with their own details attached.
 * That scoping is the privacy boundary in the UI layer — RLS already
 * filters what a crew device can read, and this makes the same
 * guarantee hold on the producer side, where the caller legitimately
 * holds everyone's rows and renders them per person.
 *
 * Pass null for an unscoped list of every leg with no personal details
 * attached (a producer overview that lists passengers separately);
 * attaching an arbitrary passenger there would print one crew member's
 * booking reference under someone else's name.
 *
 * Directions with no legs are omitted, so a one-way-only booking
 * doesn't render an empty "Return" header.
 */
export function buildItineraries(
  legs: FlightLeg[],
  passengers: FlightPassenger[],
  briefingCrewId: string | null,
): FlightItinerary[] {
  const byFlight = new Map<string, FlightPassenger>();
  if (briefingCrewId) {
    for (const p of passengers) {
      if (p.briefing_crew_id !== briefingCrewId) continue;
      byFlight.set(p.flight_id, p);
    }
  }

  const isVisible = (leg: FlightLeg) => (briefingCrewId ? byFlight.has(leg.id) : true);

  return FLIGHT_DIRECTIONS.map((direction) => ({
    direction,
    legs: legs
      .filter((leg) => leg.direction === direction && hasFlightContent(leg) && isVisible(leg))
      .sort(compareLegs)
      .map((leg) => ({ leg, passenger: byFlight.get(leg.id) ?? null })),
  })).filter((itinerary) => itinerary.legs.length > 0);
}

/** How many legs each person has, for the "3 flights" badge in the editor. */
export function legCountByCrewId(passengers: FlightPassenger[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const p of passengers) {
    counts[p.briefing_crew_id] = (counts[p.briefing_crew_id] ?? 0) + 1;
  }
  return counts;
}

// ── Validation ───────────────────────────────────────────────────────

export interface FlightIssue {
  legId: string;
  field: "arrival_at" | "departure_at" | "route" | "passengers";
  message: string;
}

/**
 * Problems worth warning the producer about before publishing. These
 * are warnings, not save blockers: a producer often knows only the
 * flight number on the day they book, and must be able to save that and
 * fill the rest later.
 */
export function flightLegIssues(leg: FlightLeg, passengerCount: number): FlightIssue[] {
  const issues: FlightIssue[] = [];

  if (leg.departure_at && leg.arrival_at) {
    const departure = Date.parse(leg.departure_at);
    const arrival = Date.parse(leg.arrival_at);
    if (Number.isFinite(departure) && Number.isFinite(arrival) && arrival < departure) {
      issues.push({
        legId: leg.id,
        field: "arrival_at",
        message: "Arrival is before departure.",
      });
    }
  }

  if (!leg.departure_airport && !leg.departure_airport_name) {
    issues.push({ legId: leg.id, field: "route", message: "Departure airport is missing." });
  }
  if (!leg.arrival_airport && !leg.arrival_airport_name) {
    issues.push({ legId: leg.id, field: "route", message: "Arrival airport is missing." });
  }
  if (!leg.departure_at) {
    issues.push({ legId: leg.id, field: "departure_at", message: "Departure time is missing." });
  }
  if (passengerCount === 0) {
    issues.push({
      legId: leg.id,
      field: "passengers",
      message: "No crew member is on this flight yet.",
    });
  }

  return issues;
}

/** All issues across a briefing's legs, for the producer's summary. */
export function flightIssues(legs: FlightLeg[], passengers: FlightPassenger[]): FlightIssue[] {
  const counts: Record<string, number> = {};
  for (const p of passengers) counts[p.flight_id] = (counts[p.flight_id] ?? 0) + 1;
  return legs.flatMap((leg) => flightLegIssues(leg, counts[leg.id] ?? 0));
}
